import { EquipmentProperties } from "../../ReportDialog/ExceptionReport/types"

type Operator =
  | { eq: any }
  | { contains: any[] }
  | { notContains: any[] }
  | { ne: number }
  | { ge: number }
  | { le: number }
  | { gt: number }
  | { lt: number }

type Condition = {
  operator: Operator
}

type ApplicabilityNode =
  | { And: Record<string, Condition | ApplicabilityNode> }
  | { Or: Record<string, Condition | ApplicabilityNode> }
  | Record<string, Condition> // fallback for basic blocks

type Characteristic = {
  targetKey: string
  value: any
}

export function evaluateApplicability(
  applicability: ApplicabilityNode | null | undefined,
  characteristics: Characteristic[]
): boolean {
  if (!applicability) {
    return true
  }
  const charMap: Record<string, any> = characteristics.reduce(
    (acc, { targetKey, value }) => {
      acc[targetKey] = value
      return acc
    },
    {} as Record<string, any>
  )

  const evaluateCondition = (charKey: string, rule: Condition): boolean => {
    const actual = charMap?.[charKey]
    const operator = rule?.operator

    if ("eq" in operator) {
      return actual === operator.eq
    }
    if ("ne" in operator) {
      return actual !== operator.ne
    }
    if ("contains" in operator) {
      return operator.contains.includes(actual)
    }
    if ("notContains" in operator) {
      return !operator.notContains.includes(actual)
    }
    if ("gt" in operator) {
      return actual > operator.gt
    }
    if ("lt" in operator) {
      return actual < operator.lt
    }
    if ("le" in operator) {
      return actual <= operator.le
    }
    if ("ge" in operator) {
      return actual >= operator.ge
    }
    return false
  }

  const evaluateGroup = (group: ApplicabilityNode): boolean => {
    if ("And" in group) {
      return Object.entries(group.And).every(([key, value]) => {
        if (key === "And" || key === "Or") {
          return evaluateGroup({ [key]: value })
        }
        if ("operator" in value) {
          return evaluateCondition(key, value as Condition)
        }
        return evaluateGroup(value as ApplicabilityNode)
      })
    }

    if ("Or" in group) {
      return Object.entries(group.Or).some(([key, value]) => {
        if (key === "And" || key === "Or") {
          return evaluateGroup({ [key]: value })
        }
        if ("operator" in value) {
          return evaluateCondition(key, value as Condition)
        }
        return evaluateGroup(value as ApplicabilityNode)
      })
    }

    // fallback block
    return Object.entries(group).every(([key, value]) =>
      evaluateCondition(key, value as Condition)
    )
  }

  return evaluateGroup(applicability)
}

export const getCircuitAndCompressorInstances = (
  propertyArray: EquipmentProperties[]
) => {
  const circuitCount = propertyArray?.find(
    ({ targetKey }) => targetKey?.toLowerCase() === "circuitcount"
  )?.value
  const circuitInstanceNumbers = []
  const displayCKTInstances = []
  const circuitInstances = []
  const compressorInstances = []
  const instanceObject = {
    circuitCount: parseInt(circuitCount)
  }
  if (circuitCount && parseInt(circuitCount) >= 1) {
    for (let i = 1; i <= parseInt(circuitCount); i++) {
      circuitInstances.push(`CKT${i}`)
      circuitInstanceNumbers.push(i)
      const compKey = `CompressorCountCKT${i}`
      instanceObject[compKey] = parseInt(
        propertyArray?.find(
          ({ targetKey }) => targetKey?.toLowerCase() === compKey?.toLowerCase()
        )?.value || "0"
      )
    }
    displayCKTInstances.push(...circuitInstances)
    circuitInstances?.forEach((instance: string) => {
      const formedTargetKey = `compressorcount${instance?.toLowerCase()}`
      const compressorCount = propertyArray?.find(
        ({ targetKey }) => targetKey?.toLowerCase() === formedTargetKey
      )?.value
      if (compressorCount && parseInt(compressorCount) >= 1) {
        for (let i = 1; i <= parseInt(compressorCount); i++) {
          const suffix = String?.fromCharCode(i + 96)?.toUpperCase()
          compressorInstances.push(`${instance?.slice(3)}${suffix}`)
        }
      }
    })
  }
  if (circuitInstances?.length === 1) {
    circuitInstances[0] = "CKT"
  }

  return {
    instanceObject,
    circuitInstances,
    circuitInstanceNumbers,
    compressorInstances
  }
}
